'use client';

import React, { useEffect } from 'react';

type HotkeyAction =
  | 'command.center.toggle'
  | 'command.center.search'
  | 'mindmap.toggle'
  | 'excalidraw.toggle'
  | 'feedback.toggle'
  | 'audio.toggle';

type Modifier = 'primary' | 'alt' | 'shift';

interface HotkeyBinding {
  action: HotkeyAction;
  key: string;
  modifiers: Modifier[];
}

const defaultBindings: HotkeyBinding[] = [
  { action: 'command.center.toggle', key: 'k', modifiers: ['primary'] },
  { action: 'command.center.search', key: 'k', modifiers: ['primary', 'alt'] },
  { action: 'mindmap.toggle', key: 'm', modifiers: ['primary'] },
  { action: 'excalidraw.toggle', key: 'e', modifiers: ['primary'] },
  { action: 'feedback.toggle', key: 'f', modifiers: ['primary', 'shift'] },
  { action: 'audio.toggle', key: 'r', modifiers: ['primary', 'alt'] },
];

function matchBinding(binding: HotkeyBinding, event: KeyboardEvent): boolean {
  const isMac = typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.platform ?? '');
  const primaryDown = isMac ? event.metaKey : event.ctrlKey;
  const altDown = event.altKey;
  const shiftDown = event.shiftKey;

  const requiredPrimary = binding.modifiers.includes('primary');
  const requiredAlt = binding.modifiers.includes('alt');
  const requiredShift = binding.modifiers.includes('shift');

  const rawKey = (event.key || '').toLowerCase();
  const rawCode = (event.code || '').toLowerCase();
  const expectedKey = binding.key.toLowerCase();

  // On some platforms (notably macOS), holding Alt/Option changes event.key (e.g., Option+K => "˚"),
  // but event.code stays stable ("KeyK"). To keep shortcuts reliable, accept either a direct key
  // match or a matching physical code for letter keys.
  const keyMatches =
    rawKey === expectedKey ||
    (rawCode === `key${expectedKey}` && expectedKey.length === 1);
  if (!keyMatches) return false;

  if (requiredPrimary !== primaryDown) return false;
  if (requiredAlt !== altDown) return false;
  if (requiredShift !== shiftDown) return false;

  return true;
}

function dispatchHotkeyAction(action: HotkeyAction) {
  const name = `hotkey:${action}`;
  try {
    window.dispatchEvent(new CustomEvent(name));
  } catch {
    // ignore
  }
}

export function GlobalHotkeysProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      for (const binding of defaultBindings) {
        if (matchBinding(binding, event)) {
          event.preventDefault();
          event.stopPropagation();
          dispatchHotkeyAction(binding.action);
          break;
        }
      }
    };

    document.addEventListener('keydown', handler, true);
    return () => {
      document.removeEventListener('keydown', handler, true);
    };
  }, []);

  return <>{children}</>;
}
